import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';

import { getSmsDashboardScheduledMessagesKey } from './useScheduledMessages';
import { showResult } from '../utils';

import type { DigitalEngagementType } from 'infrastructure/data-sources/digital-engagement';
import type { IScheduledMessagesListRes } from 'infrastructure/interfaces';

interface Props {
  onSuccess: () => void;
}
interface FnProps {
  conversationId?: number;
  patientGuid?: string;
  message: string;
  from: string;
  patientPhone: string;
  scheduledTime: string | null;
  digitalEngagement?: DigitalEngagementType;
}

const sendScheduledMessageFn = async ({
  conversationId,
  patientGuid,
  message,
  from,
  patientPhone,
  scheduledTime,
  digitalEngagement,
}: FnProps) => {
  const API_SCHEDULED_MESSAGES = `core-2/conversations/scheduled-messages`;

  const { data, error } = await dataService.createOne(API_SCHEDULED_MESSAGES, {
    patientPhone,
    patientGuid,
    message,
    from,
    conversationId,
    scheduledTime,
    digitalEngagement,
  });

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useSendScheduledMessage = (_: unknown, { onSuccess }: Props) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading, ...other } = useMutation({
    mutationFn: sendScheduledMessageFn,
    onSuccess(data, variables) {
      onSuccess();

      if (variables.conversationId) {
        const scheduledMessagesKey = getSmsDashboardScheduledMessagesKey({
          conversationId: variables.conversationId,
        });
        queryClient.setQueryData(
          scheduledMessagesKey,
          (oldMessages: IScheduledMessagesListRes | undefined) => {
            if (!oldMessages) return;
            return {
              ...oldMessages,
              items: [...oldMessages.items, data],
            };
          },
        );
      }
    },
  });

  return {
    sendScheduledMessage: mutateAsync,
    sendScheduledMessageLoading: isLoading,
    ...other,
  };
};
